// eventApiService.js
// Service for fetching real events from the validation API

import axios from './authService';
import { API_ENDPOINTS } from '../config/apiConfig';

/**
 * Fetch events for validation from the API
 * @param {number} flowDbId - The database ID of the flow (test_flow_id)
 * @param {string} deviceId - The device ID to filter events
 * @param {string} marketId - The market ID to filter events
 * @param {number} timestampStart - Start timestamp in milliseconds
 * @param {number} timestampEnd - End timestamp in milliseconds
 * @returns {Promise<Array>} Array of events in the format expected by validation logic
 */
export const fetchEventsForValidation = async (
  flowDbId,
  deviceId,
  marketId,
  timestampStart,
  timestampEnd
) => {
  try {
    // Build query parameters
    const params = new URLSearchParams({
      test_flow_id: flowDbId,
      device_id: deviceId,
      market_id: marketId,
      timestamp_start: timestampStart,
      timestamp_end: timestampEnd
    });

    const url = `${API_ENDPOINTS.GET_EVENTS_FOR_VALIDATION}?${params.toString()}`;

    console.log('Fetching events from API:', url);

    const response = await axios.get(url);
    const result = response.data;

    // Extract events from response
    const apiEvents = result?.data?.events_json || [];

    console.log(`Received ${apiEvents.length} events from API`);

    // Transform API events to match the format expected by validation logic
    const transformedEvents = apiEvents.map(apiEvent => {
      // Determine event type
      let eventType = 'inbound';
      if (apiEvent.type === 'outbound' && apiEvent.outbound_type_name) {
        eventType = `outbound-${apiEvent.outbound_type_name}`;
      } else if (apiEvent.type === 'outbound') {
        eventType = 'outbound';
      }

      // Convert event_time string to timestamp in milliseconds
      // Database stores times in UTC, so we need to explicitly mark them as UTC
      const timestamp = apiEvent.event_time
        ? new Date(apiEvent.event_time.replace(' ', 'T') + 'Z').getTime()
        : Date.now();

      // Handle event data structure
      let eventData;
      let proxyData = null;
      let topLevelEventProps = {};

      if (apiEvent.event_object) {
        // NEW FORMAT: Holistically extract ALL event_object properties
        const { data, ...restOfEventObject } = apiEvent.event_object;

        // Store top-level event_object properties (event_type, and any future fields)
        topLevelEventProps = restOfEventObject;

        // Use the nested data object directly (preserves full structure)
        eventData = data || {};
      } else {
        // LEGACY FORMAT: Preserve nested structure instead of spreading
        eventData = {
          event_name: apiEvent.event_name,
          event_type: apiEvent.event_type,
          custom_event_type: apiEvent.custom_event_type,
          // Keep custom_attributes nested (not spread)
          custom_attributes: apiEvent.custom_attributes || {},
          // Keep device_info/device_current_state nested (not spread)
          device_current_state: apiEvent.device_current_state || apiEvent.device_info || {},
          // Include other top-level fields
          mpid: apiEvent.mpid,
          event_id: apiEvent.event_id
        };
      }

      // Extract proxy data from event_object_proxy (same structure as event_object)
      if (apiEvent.event_object_proxy) {
        const { data: proxyDataNested } = apiEvent.event_object_proxy;
        proxyData = proxyDataNested || {};
      }

      // Return in the format expected by validation logic
      // Top-level event_object properties (like event_type) are preserved at top level
      return {
        // Top-level properties from event_object (event_type from mParticle, etc.)
        ...topLevelEventProps,

        // Internal metadata for our app logic
        event_name: apiEvent.event_name,
        event_direction: eventType,  // Our internal direction: "inbound", "outbound-epsilon"
        timestamp: timestamp,
        device_id: apiEvent.device_id_1 || deviceId,
        is_blocked_event: apiEvent.is_blocked_event === 'true' || apiEvent.is_blocked_event === true,
        blocked_user_attributes: apiEvent.blocked_user_attributes || null,

        // Nested data object
        data: eventData,

        // Proxy data (from event_object_proxy, display-only)
        proxyData: proxyData
      };
    });

    console.log('Transformed events:', transformedEvents);

    return transformedEvents;

  } catch (error) {
    console.error('Error fetching events for validation:', error);
    throw new Error(`Failed to fetch events: ${error.message}`);
  }
};
